import { getAnalyticsEvents } from './analyticsService';
import { getCollection } from './firestoreService';
import { getAllSubscriptions } from './subscriptionService';
import { differenceInDays } from 'date-fns';

export interface LearningInsightsMetrics {
  totalViews: number;
  uniqueUsers: number;
  totalLoads: number;
  totalRefreshes: number;
  totalNavigations: number;
  averageTimeSpent: number;
  timePeriodDistribution: Record<string, number>;
}

export interface KnowledgeMapMetrics {
  totalViews: number;
  uniqueUsers: number;
  totalLoads: number;
  totalRefreshes: number;
  totalNodeTaps: number;
  totalNavigations: number;
  averageTimeSpent: number;
  nodeStatusDistribution: Record<string, number>;
}

export interface FeatureAdoption {
  featureName: string;
  adoptionRate: number;
  uniqueUsers: number;
  totalUsage: number;
  discoveryTime: number; // avg days from signup to first use
}

export interface FeatureEffectiveness {
  featureName: string;
  retentionCorrelation: number;
  revenueCorrelation: number;
  highValueUserUsage: number;
}

export interface FeatureUsageAnalytics {
  featureAdoption: FeatureAdoption[];
  featureEffectiveness: FeatureEffectiveness[];
  featureAbandonmentRate: Map<string, number>;
}

export const getFeatureUsageAnalytics = async (
  days: number = 30
): Promise<FeatureUsageAnalytics> => {
  try {
    const events = await getAnalyticsEvents(days * 2);
    const users = await getCollection('users', undefined, undefined, undefined, 10000);
    const subscriptions = await getAllSubscriptions();

    // Define features and their event patterns
    const features = [
      { name: 'Assistant', patterns: ['assistant', 'ai_chat', 'assistant_query'] },
      { name: 'Search', patterns: ['search', 'assistant_search'] },
      { name: 'Personalized Categories', patterns: ['personalized_category', 'category_used'] },
      { name: 'Gamification', patterns: ['achievement', 'xp_earned', 'level_up'] },
      { name: 'Settings', patterns: ['settings', 'preferences'] },
      { name: 'Bookmarks', patterns: ['bookmark', 'save'] },
      { name: 'Learning Insights', patterns: ['learning_insights', 'insights_viewed', 'insights_loaded', 'insights_refreshed', 'insights_navigate', 'insights_time_period', 'insights_time_spent'] },
      { name: 'Knowledge Map', patterns: ['knowledge_map', 'knowledge_map_viewed', 'knowledge_map_loaded', 'knowledge_map_refreshed', 'knowledge_map_node', 'knowledge_map_navigate', 'knowledge_map_time_spent'] },
    ];

    const featureUsageMap = new Map<string, {
      uniqueUsers: Set<string>;
      totalUsage: number;
      firstUseTimes: Map<string, Date>; // userId -> first use date
    }>();

    const userSignupDates = new Map<string, Date>();
    users.forEach((user: any) => {
      const signupDate = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
      userSignupDates.set(user.id, signupDate);
    });

    // Track feature usage
    events.forEach((event) => {
      features.forEach((feature) => {
        const matchesFeature = feature.patterns.some((pattern) =>
          event.eventName.toLowerCase().includes(pattern.toLowerCase())
        );

        if (matchesFeature) {
          if (!featureUsageMap.has(feature.name)) {
            featureUsageMap.set(feature.name, {
              uniqueUsers: new Set(),
              totalUsage: 0,
              firstUseTimes: new Map(),
            });
          }

          const featureData = featureUsageMap.get(feature.name)!;
          featureData.uniqueUsers.add(event.userId);
          featureData.totalUsage++;

          if (!featureData.firstUseTimes.has(event.userId)) {
            featureData.firstUseTimes.set(event.userId, event.timestamp);
          }
        }
      });
    });

    // Calculate adoption metrics
    const totalUsers = users.length;
    const featureAdoption: FeatureAdoption[] = [];

    featureUsageMap.forEach((data, featureName) => {
      const adoptionRate = totalUsers > 0 ? (data.uniqueUsers.size / totalUsers) * 100 : 0;

      // Calculate average discovery time
      let totalDiscoveryTime = 0;
      let discoveryCount = 0;

      data.firstUseTimes.forEach((firstUseDate, userId) => {
        const signupDate = userSignupDates.get(userId);
        if (signupDate) {
          const daysToFirstUse = differenceInDays(firstUseDate, signupDate);
          if (daysToFirstUse >= 0 && daysToFirstUse < 365) {
            totalDiscoveryTime += daysToFirstUse;
            discoveryCount++;
          }
        }
      });

      const discoveryTime = discoveryCount > 0 ? totalDiscoveryTime / discoveryCount : 0;

      featureAdoption.push({
        featureName,
        adoptionRate,
        uniqueUsers: data.uniqueUsers.size,
        totalUsage: data.totalUsage,
        discoveryTime,
      });
    });

    // Feature effectiveness (simplified)
    const highValueUsers = new Set(
      subscriptions.filter((s: any) => s.status === 'active').map((s: any) => s.userId)
    );

    const featureEffectiveness: FeatureEffectiveness[] = featureAdoption.map((adoption) => {
      const featureData = featureUsageMap.get(adoption.featureName)!;
      const highValueUsage = Array.from(featureData.uniqueUsers).filter((uid) =>
        highValueUsers.has(uid)
      ).length;

      return {
        featureName: adoption.featureName,
        retentionCorrelation: 0, // Would need retention data correlation
        revenueCorrelation: 0, // Would need revenue correlation
        highValueUserUsage: highValueUsers.size > 0 ? (highValueUsage / highValueUsers.size) * 100 : 0,
      };
    });

    // Feature abandonment (users who used once but not again in last 30 days)
    const featureAbandonmentRate = new Map<string, number>();
    // Simplified calculation

    return {
      featureAdoption: featureAdoption.sort((a, b) => b.adoptionRate - a.adoptionRate),
      featureEffectiveness,
      featureAbandonmentRate,
    };
  } catch (error) {
    console.error('Error calculating feature usage analytics:', error);
    throw error;
  }
};

/// Get detailed metrics for Learning Insights feature
export const getLearningInsightsMetrics = async (
  days: number = 30
): Promise<LearningInsightsMetrics> => {
  try {
    const events = await getAnalyticsEvents(days);
    
    const insightsEvents = events.filter((e) =>
      e.eventName.includes('learning_insights')
    );
    
    const uniqueUsers = new Set<string>();
    let totalViews = 0;
    let totalLoads = 0;
    let totalRefreshes = 0;
    let totalNavigations = 0;
    let totalTimeSpent = 0;
    let timeSpentCount = 0;
    const timePeriodDistribution: Record<string, number> = {};
    
    insightsEvents.forEach((event) => {
      if (event.userId) {
        uniqueUsers.add(event.userId);
      }
      
      if (event.eventName === 'learning_insights_viewed' || event.eventName === 'learning_insights_loaded') {
        totalViews++;
        if (event.eventName === 'learning_insights_loaded') {
          totalLoads++;
        }
      } else if (event.eventName === 'learning_insights_refreshed') {
        totalRefreshes++;
      } else if (event.eventName === 'learning_insights_navigate_to_path') {
        totalNavigations++;
      } else if (event.eventName === 'learning_insights_time_spent') {
        const timeSpent = event.properties?.time_spent_seconds as number || 0;
        if (timeSpent > 0) {
          totalTimeSpent += timeSpent;
          timeSpentCount++;
        }
      }
      
      if (event.eventName === 'learning_insights_time_period_changed' || event.eventName === 'learning_insights_viewed') {
        const period = event.properties?.time_period as string || event.properties?.new_period as string || 'unknown';
        timePeriodDistribution[period] = (timePeriodDistribution[period] || 0) + 1;
      }
    });
    
    return {
      totalViews,
      uniqueUsers: uniqueUsers.size,
      totalLoads,
      totalRefreshes,
      totalNavigations,
      averageTimeSpent: timeSpentCount > 0 ? totalTimeSpent / timeSpentCount : 0,
      timePeriodDistribution,
    };
  } catch (error) {
    console.error('Error getting Learning Insights metrics:', error);
    throw error;
  }
};

/// Get detailed metrics for Knowledge Map feature
export const getKnowledgeMapMetrics = async (
  days: number = 30
): Promise<KnowledgeMapMetrics> => {
  try {
    const events = await getAnalyticsEvents(days);
    
    const knowledgeMapEvents = events.filter((e) =>
      e.eventName.includes('knowledge_map')
    );
    
    const uniqueUsers = new Set<string>();
    let totalViews = 0;
    let totalLoads = 0;
    let totalRefreshes = 0;
    let totalNodeTaps = 0;
    let totalNavigations = 0;
    let totalTimeSpent = 0;
    let timeSpentCount = 0;
    const nodeStatusDistribution: Record<string, number> = {};
    
    knowledgeMapEvents.forEach((event) => {
      if (event.userId) {
        uniqueUsers.add(event.userId);
      }
      
      if (event.eventName === 'knowledge_map_viewed' || event.eventName === 'knowledge_map_loaded') {
        totalViews++;
        if (event.eventName === 'knowledge_map_loaded') {
          totalLoads++;
        }
      } else if (event.eventName === 'knowledge_map_refreshed') {
        totalRefreshes++;
      } else if (event.eventName === 'knowledge_map_node_tapped') {
        totalNodeTaps++;
        const status = event.properties?.node_status as string || 'unknown';
        nodeStatusDistribution[status] = (nodeStatusDistribution[status] || 0) + 1;
      } else if (event.eventName === 'knowledge_map_navigate_to_path') {
        totalNavigations++;
      } else if (event.eventName === 'knowledge_map_time_spent') {
        const timeSpent = event.properties?.time_spent_seconds as number || 0;
        if (timeSpent > 0) {
          totalTimeSpent += timeSpent;
          timeSpentCount++;
        }
      }
    });
    
    return {
      totalViews,
      uniqueUsers: uniqueUsers.size,
      totalLoads,
      totalRefreshes,
      totalNodeTaps,
      totalNavigations,
      averageTimeSpent: timeSpentCount > 0 ? totalTimeSpent / timeSpentCount : 0,
      nodeStatusDistribution,
    };
  } catch (error) {
    console.error('Error getting Knowledge Map metrics:', error);
    throw error;
  }
};
